'use es6';

import { connect as connectFlux } from 'general-store';
import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import { memo, useEffect, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { connect, useSelector } from 'react-redux';
import Env from 'enviro';
import { selectAllScopes } from 'dashboard-lib/public/userInfo';
import { selectHasCustomReportingAcccess } from 'dashboard-lib/public/userInfo/scopes';
import { CUSTOM_DASHBOARD_COUNT, CUSTOM_DASHBOARD_LIMIT, DASHBOARD_CREATED_AT_TIMESTAMP, DASHBOARD_IS_CUSTOM, MAX_USER_ASSET_PERMISSION, USER_DASHBOARD_PERMISSION } from 'dashboard-lib/private/action-problem-solution/params/all-params';
import { getWidgetsWithDefault, hasNoWidgets, isCustomDashboard } from 'dashboard-lib/public/dashboard/dashboard-logic';
import { getCreatedAt, getId as getDashboardId, getWidgetCount } from 'dashboard-lib/public/dashboard/dashboard-schema';
import { canEditDashboardContents } from 'dashboard-lib/public/enable-action/edit-dashboard-contents';
import compose from 'transmute/compose';
import { ALL_REPORTS_SETTLED, PRIORITY_REPORTS_SETTLED } from '../../lib/page-actions';
import GenericEmptyState from '../onboarding/GenericEmptyState';
import LoadingMessage from '../shared/LoadingMessage';
import WidgetGrid from './WidgetGrid';
import { selectDashboardPermission } from 'dashboard-lib/public/permission/permission-selector';
import { useCustomDashboardLimit } from 'dashboard-components/context/use-custom-dashboard-limit';
import createHocName from 'dashboard-components/decorator/createHocName';
import toClass from '../../decorators/toClass';
import { DashboardCountDependency } from '../../dependencies/DashboardsDependency';
import { selectUserMaxAssetPermission } from 'dashboard-lib/public/userInfo/permissions';
import { selectIsSuperUser } from 'dashboard-lib/public/userInfo/scopes';
import { selectDashboardFilters } from '../../ducks/dashboardFilters';
import { usePlatformDashboardDispatch } from 'platform-dashboard-ui/store';
import { fetchDataSourcesProperties } from 'platform-dashboard-ui/ducks/dataSourcePropertiesSlice';
import { WidgetGridErrorBoundary } from './WidgetGridErrorBoundary';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
const useTrackSettled = (allReportsSettled, priorityReportsSettled, dashboard) => {
  const [trackedAll, setTrackedAll] = useState(false);
  const [trackedPriority, setTrackedPriority] = useState(false);
  const widgetCount = getWidgetCount(dashboard);
  useEffect(() => {
    if (allReportsSettled && !trackedAll) {
      Raven.capturePageEvent(PRIORITY_REPORTS_SETTLED, {
        extra: {
          widgetCount
        }
      });
      setTrackedAll(true);
    }
    if (priorityReportsSettled && !trackedPriority) {
      Raven.capturePageEvent(ALL_REPORTS_SETTLED, {
        extra: {
          widgetCount
        }
      });
      setTrackedPriority(true);
    }
  }, [allReportsSettled, priorityReportsSettled, widgetCount, trackedAll, trackedPriority]);
};
const WidgetGridContainer = props => {
  const {
    allReportsSettled,
    dashboard,
    fetched,
    canEditReports,
    priorityReportsSettled
  } = props;
  useTrackSettled(allReportsSettled, priorityReportsSettled, dashboard);
  const dashboardId = getDashboardId(dashboard);
  const scopes = useSelector(selectAllScopes);
  const platformDispatch = usePlatformDashboardDispatch();
  const hasCustomReportingAccess = useSelector(selectHasCustomReportingAcccess);
  const dashboardFilters = useSelector(selectDashboardFilters(dashboardId));
  useEffect(() => {
    platformDispatch(fetchDataSourcesProperties(dashboardFilters.map(filter => filter.dataSource), hasCustomReportingAccess, scopes));
  }, [dashboardFilters, platformDispatch, hasCustomReportingAccess, scopes]);
  if (!fetched) {
    return /*#__PURE__*/_jsx(LoadingMessage, {});
  }
  const empty = hasNoWidgets(dashboard);
  return /*#__PURE__*/_jsx("section", {
    "data-onboarding": "widgets",
    children: empty ? /*#__PURE__*/_jsx(NavMarker, {
      name: "DASHBOARD_EMPTY_STATE",
      children: /*#__PURE__*/_jsx(GenericEmptyState, {
        dashboardId: dashboardId
      })
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "WIDGET_GRID_WRAPPER_LOADED",
      children: /*#__PURE__*/_jsx(WidgetGridErrorBoundary, {
        dashboardId: dashboardId,
        children: /*#__PURE__*/_jsx(WidgetGrid, {
          dashboard: dashboard,
          widgets: getWidgetsWithDefault(dashboard),
          canEditWidgets: canEditReports
        })
      })
    })
  });
};
WidgetGridContainer.propTypes = {
  dashboard: PropTypes.instanceOf(ImmutableMap).isRequired,
  allReportsSettled: PropTypes.bool.isRequired,
  priorityReportsSettled: PropTypes.bool.isRequired,
  fetched: PropTypes.bool.isRequired,
  canEditReports: PropTypes.bool.isRequired
};
const withCanEditReports = Subject => {
  const Enhanced = props => {
    const {
      dashboard,
      dashboardsCount,
      dashboardPermission,
      maxUserAssetPermission,
      isSuperUser
    } = props;
    const customDashboardLimit = useCustomDashboardLimit();
    const params = {
      [USER_DASHBOARD_PERMISSION]: dashboardPermission,
      [MAX_USER_ASSET_PERMISSION]: maxUserAssetPermission,
      [DASHBOARD_IS_CUSTOM]: isCustomDashboard(dashboard),
      [DASHBOARD_CREATED_AT_TIMESTAMP]: getCreatedAt(dashboard),
      [CUSTOM_DASHBOARD_COUNT]: dashboardsCount,
      [CUSTOM_DASHBOARD_LIMIT]: customDashboardLimit
    };
    const isDebugging = Env.debug('dashboards') && isSuperUser;
    const newProps = Object.assign({}, props, {
      canEditReports: canEditDashboardContents(params) || isDebugging
    });
    return /*#__PURE__*/_jsx(Subject, Object.assign({}, newProps));
  };
  Enhanced.displayName = createHocName('withCanEditReports', Subject);
  return Enhanced;
};
export default compose(connect((state, ownProps) => {
  const {
    dashboard,
    fetched
  } = ownProps;
  const dashboardId = getDashboardId(dashboard);
  const dashboardPermission = selectDashboardPermission(dashboardId)(state);
  const maxUserAssetPermission = selectUserMaxAssetPermission(state);
  const isSuperUser = selectIsSuperUser(state);
  return {
    allReportsSettled: fetched,
    priorityReportsSettled: fetched,
    dashboardPermission,
    maxUserAssetPermission,
    isSuperUser
  };
}), connectFlux({
  dashboardsCount: DashboardCountDependency
}), toClass, withCanEditReports, memo)(WidgetGridContainer);